/** Symbole OTAN du pont (APP-6, franchissement : deux traits parallèles aux extrémités évasées), dessiné
 *  horizontal et tourné par la carte le long de la route ; pont détruit : même symbole barré d'une croix
 *  (marque « détruit » d'APP-6), comme les images BridgeOk / BridgeNo de l'iPad (40 × 24 points). */

export const BRIDGE_IMAGE = "gx-bridge";
export const BRIDGE_DESTROYED_IMAGE = "gx-bridge-destroyed";

const WIDTH = 40;
const HEIGHT = 24;
const INK = "#1b1d21"; // --color-ink
const RED = "#c83e3e"; // --color-brand : croix du pont détruit

interface ImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void;
}

function strokeBridge(ctx: CanvasRenderingContext2D) {
  // Tablier : deux traits parallèles ; culées : extrémités évasées vers l'extérieur.
  ctx.beginPath();
  ctx.moveTo(4, 3);
  ctx.lineTo(10, 8.5);
  ctx.lineTo(30, 8.5);
  ctx.lineTo(36, 3);
  ctx.moveTo(4, 21);
  ctx.lineTo(10, 15.5);
  ctx.lineTo(30, 15.5);
  ctx.lineTo(36, 21);
  ctx.stroke();
}

function strokeCross(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.moveTo(8, 2.5);
  ctx.lineTo(32, 21.5);
  ctx.moveTo(32, 2.5);
  ctx.lineTo(8, 21.5);
  ctx.stroke();
}

export function drawBridge(destroyed: boolean, pixelRatio: number): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(WIDTH * pixelRatio);
  canvas.height = Math.round(HEIGHT * pixelRatio);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponible");
  ctx.scale(pixelRatio, pixelRatio);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Liseré blanc sous le trait : lisible sur tous les fonds (plan, ortho, forêts).
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 5.4;
  strokeBridge(ctx);
  if (destroyed) strokeCross(ctx);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.4;
  strokeBridge(ctx);
  if (destroyed) {
    ctx.strokeStyle = RED;
    ctx.lineWidth = 2.8;
    strokeCross(ctx);
  }
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Ajoute l'image demandée (« styleimagemissing ») si c'est un symbole de pont. */
export function provideBridgeImage(map: ImageHost, id: string): boolean {
  if (id !== BRIDGE_IMAGE && id !== BRIDGE_DESTROYED_IMAGE) return false;
  if (map.hasImage(id)) return true;
  const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
  map.addImage(id, drawBridge(id === BRIDGE_DESTROYED_IMAGE, pixelRatio), { pixelRatio });
  return true;
}

/** Image du symbole selon l'état du pont dans le dossier (clés « lat_lon » des ponts détruits). */
export function bridgeImageExpression(destroyed: string[]): unknown {
  return destroyed.length
    ? ["case", ["in", ["get", "key"], ["literal", destroyed]], BRIDGE_DESTROYED_IMAGE, BRIDGE_IMAGE]
    : BRIDGE_IMAGE;
}

/** Aperçu SVG du symbole (fiche du pont, calques). */
export function bridgeSvg(destroyed: boolean, width = 26): string {
  const height = (width * HEIGHT) / WIDTH;
  const cross = destroyed
    ? `<path d="M8 2.5 32 21.5M32 2.5 8 21.5" stroke="${RED}" stroke-width="2.8" stroke-linecap="round"/>`
    : "";
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${WIDTH} ${HEIGHT}" fill="none">` +
    `<path d="M4 3 10 8.5H30L36 3M4 21 10 15.5H30L36 21" stroke="${INK}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>` +
    `${cross}</svg>`
  );
}
